import { useMemo } from 'react';

/**
 * Dependency-free SVG sparkline used by the anti-detection 16-tile
 * grid. Deliberately avoids pulling in a new charting library so the
 * BO bundle stays small and the security review surface unchanged.
 *
 * Inputs:
 *   - `values`   — non-negative numeric counts, one per hour bucket.
 *                  Empty array or all-zero values render an "empty"
 *                  baseline so the tile never looks broken.
 *   - `color`    — stroke colour (default Antd blue). The fill is
 *                  `color` at low alpha for a soft area look.
 *   - `width`    — pixel width. Default 160 fits the tile footer.
 *   - `height`   — pixel height. Default 36 matches the spec mockup.
 *   - `ariaLabel`— accessibility label for screen readers — required
 *                  per spec §4.2 accessibility note.
 *
 * Returns an `<svg>` with a stroke path + area fill polygon. Pure
 * presentation — no state, no fetch, no Antd dependency, so tests
 * can render this in isolation.
 */
export interface SparklineProps {
  values: number[];
  color?: string;
  width?: number;
  height?: number;
  ariaLabel: string;
}

export function Sparkline({
  values,
  color = '#1677ff',
  width = 160,
  height = 36,
  ariaLabel,
}: SparklineProps) {
  const { linePath, areaPath } = useMemo(
    () => buildSparklinePaths(values, width, height),
    [values, width, height]
  );

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block' }}
    >
      <path d={areaPath} fill={color} fillOpacity={0.15} stroke="none" />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface SparklinePaths {
  linePath: string;
  areaPath: string;
}

/**
 * Pure path builder — exported so unit tests can pin the geometry
 * without a DOM. Empty / all-zero inputs collapse to a baseline
 * stroke instead of an invalid `M` directive.
 */
export function buildSparklinePaths(
  values: number[],
  width: number,
  height: number
): SparklinePaths {
  if (!values.length) {
    return {
      linePath: `M 0 ${height - 1} L ${width} ${height - 1}`,
      areaPath: `M 0 ${height} L ${width} ${height} Z`,
    };
  }
  const max = Math.max(...values, 0);
  const stepX = values.length > 1 ? width / (values.length - 1) : width;

  const points = values.map((v, i) => {
    const x = values.length > 1 ? i * stepX : width / 2;
    const safeMax = max > 0 ? max : 1;
    const normalized = max > 0 ? v / safeMax : 0;
    const y = height - 1 - normalized * (height - 2);
    return { x, y };
  });

  const linePath =
    'M ' + points.map((p) => `${round2(p.x)} ${round2(p.y)}`).join(' L ');
  const areaPath =
    'M ' +
    `${round2(points[0].x)} ${height} ` +
    points.map((p) => `L ${round2(p.x)} ${round2(p.y)}`).join(' ') +
    ` L ${round2(points[points.length - 1].x)} ${height} Z`;

  return { linePath, areaPath };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
